import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'

import { CancelButton, SpinnerButton } from '@ui/components/core'
import { Notification, NotiticationsProps } from '@ui/components/core/Notification'
import { showToast } from '@ui/components/elements/ToastManager'
import { NotificationTypeEnum } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'

interface CreateOrganizationProps {
  fetchOrganizations: () => void
  modalState: boolean
  setModalClosed: () => void
}

export function CreateOrganizationModal(props: CreateOrganizationProps) {
  const { fetchOrganizations, modalState, setModalClosed } = props

  const [name, setName] = useState('')
  const [allowedDomains, setAllowedDomains] = useState('')
  const [loading, setLoading] = useState(false)

  const [notification, setNotification] = useState<NotiticationsProps | null>(null)

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }

  const onChangeAllowedDomains = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllowedDomains(e.target.value)
  }

  const onSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(createOrganization, 1000)
  }

  const createOrganization = () => {
    const configuration = {
      url: '/v1/organizations',
      data: {
        organization: {
          name,
          email_allowed_domains: allowedDomains,
        },
      },
    }
    axios
      .post(configuration.url, configuration.data)
      .then(() => {
        fetchOrganizations()
        setModalClosed()
        setLoading(false)
        setNotification(null)

        showToast.success('Organization has been created')
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        setLoading(false)

        const errorExplanations = []
        if (err?.response?.data?.error_message) errorExplanations.push(err.response.data.error_message)
        errorExplanations.push(
          'Ensure the organization name contains only alphanumeric characters, spaces, underscores, and dashes'
        )
        if (allowedDomains.length > 0) {
          errorExplanations.push('Ensure all allowed domains are valid domain names')
          errorExplanations.push('Allowed domains should be a comma-separated list of domains')
        }

        setNotification({
          show: true,
          title: 'Failed to create organization',
          messageList: errorExplanations,
          type: NotificationTypeEnum.ERROR,
        })

        console.log('Failed to create organization: ', err)
      })
  }

  const clearInput = () => {
    setName('')
    setAllowedDomains('')
    setNotification(null)
  }

  return (
    <Transition.Root show={modalState} as={Fragment} afterLeave={clearInput}>
      <Dialog as="div" className="relative inset-0 z-40" onClose={setModalClosed}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500/75" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="mx-auto flex w-3/4 items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative min-h-[200px] w-1/2 overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:p-6">
                <Dialog.Title as="h3" className="pb-1 text-base font-semibold leading-6 text-gray-900">
                  Create Organization
                </Dialog.Title>

                <form className="mt-5 w-full sm:flex sm:items-center" onSubmit={onSubmit}>
                  <div className="grid w-full grid-cols-4 gap-4">
                    <div className="col-start-1 col-end-7">
                      <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                        Name*
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="name"
                          id="name"
                          onChange={onChangeName}
                          disabled={loading}
                          value={name}
                          maxLength={128}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="col-start-1 col-end-7 ">
                      <label htmlFor="allowedDomains" className="block text-sm font-medium leading-6 text-gray-900">
                        Allowed domains
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="allowedDomains"
                          id="allowedDomains"
                          placeholder="Comma-separated list of domains (gmail.com,yahoo.com)"
                          onChange={onChangeAllowedDomains}
                          value={allowedDomains}
                          disabled={loading}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="col-start-1 col-end-7 w-full">
                      <Notification
                        show={notification !== null}
                        type={NotificationTypeEnum.ERROR}
                        title={notification?.title || ''}
                        message={notification?.message}
                        richMessage={notification?.richMessage}
                        messageList={notification?.messageList}
                      />
                    </div>

                    <div className="col-start-5 col-end-7 flex w-full">
                      <SpinnerButton disabled={loading} loading={loading}></SpinnerButton>
                      <CancelButton onClick={setModalClosed} disabled={loading}></CancelButton>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
